import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  apiClient,
  ApiError,
  ContactMessage,
  NO_SHIRT_ANSWER,
  PaymentStatus,
  compareByFirstName,
  compareByLastName,
  formatPhone,
  splitPersonName,
  enrolmentCsvTable,
  toCsv,
  type Enrolment,
  type EnrolmentMove,
  type ContactMessage as ContactMessageT,
  type PaymentStatus as PaymentStatusT,
  type Program,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import {
  AlertTriangle,
  AtSign,
  Ban,
  CheckCircle2,
  Eye,
  Pencil,
  RotateCcw,
  TableProperties,
  Trash2,
  UserPlus,
} from "lucide-react";
import { RowAction, RowActions } from "../components/RowActions";
import { SheetLinkDialog } from "../components/SheetLinkDialog";
import { METHOD_LABEL } from "../components/ReconcilePayments";
import { ManualEnrolmentDialog } from "../components/ManualEnrolmentDialog";
import { EditEnrolmentDialog, EditHistory } from "../components/EditEnrolmentDialog";
import { MovePendingBadge, MoveRegistrationDialog } from "../components/MoveRegistrationDialog";
import { useEnrolmentUiConfig } from "../config";

/**
 * API base. The site is served from the same origin as the Worker (Vite proxies
 * `/api` in dev), so relative requests are correct. This route sits behind
 * Cloudflare Access; same-origin cookies authenticate the operator.
 */
const API_BASE = "";

/**
 * Contact messages are admin-listed at `/api/admin/contact-messages`. The shared
 * apiClient does not expose a typed method for it yet, so we issue a raw GET and
 * validate the payload with the SHARED `ContactMessage` schema (no duplicated
 * types). The table degrades gracefully if the endpoint is unavailable.
 */
const CONTACT_MESSAGES_PATH = "/api/admin/contact-messages";

type TabKey = "enrolments" | "contact";
type StatusFilter = PaymentStatusT | "all";

/**
 * How a list of people is ordered.
 *
 * "Newest first" is what the server returns and stays the default — an inbox
 * is read from the top. The by-name orders exist because an inbox is also the
 * place a host goes to FIND one family among hundreds, and a class list is
 * held, spoken and filed by surname. The names are stored in one box, so the
 * surname is derived by `compareByLastName` in @alight/contracts — the same
 * rule that fills the first/last columns in the spreadsheet, so the portal and
 * the linked sheet put a family in the same place.
 */
type NameSort = "newest" | "parent_last" | "child_last" | "child_first";

const NAME_SORT_LABELS: Record<NameSort, string> = {
  newest: "Newest first",
  parent_last: "Parent surname A–Z",
  child_last: "Child surname A–Z",
  child_first: "Child first name A–Z",
};

export function AdminSubmissions() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const [tab, setTab] = React.useState<TabKey>("enrolments");

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-heading text-xl font-bold text-ink">Submissions</h2>
        <p className="mt-1 text-sm text-muted">
          Enrolments and contact messages received through the public site.
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Submission type"
        className="flex gap-2 border-b border-border"
      >
        <TabButton
          active={tab === "enrolments"}
          onClick={() => setTab("enrolments")}
        >
          Enrolments
        </TabButton>
        <TabButton active={tab === "contact"} onClick={() => setTab("contact")}>
          Contact messages
        </TabButton>
      </div>

      {tab === "enrolments" ? (
        <EnrolmentsPanel api={api} />
      ) : (
        <ContactPanel />
      )}
    </section>
  );
}

/* ===========================================================================
 * Enrolments
 * ========================================================================= */

function EnrolmentsPanel({ api }: { api: ReturnType<typeof apiClient> }) {
  const [status, setStatus] = React.useState<StatusFilter>("all");
  const [programFilter, setProgramFilter] = React.useState<string>("all");
  const [sort, setSort] = React.useState<NameSort>("newest");
  const [search, setSearch] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [viewing, setViewing] = React.useState<Enrolment | null>(null);
  const [correcting, setCorrecting] = React.useState<Enrolment | null>(null);
  const [moving, setMoving] = React.useState<Enrolment | null>(null);
  const [closing, setClosing] = React.useState<Enrolment | null>(null);
  const [deleting, setDeleting] = React.useState<Enrolment | null>(null);
  /* UNSUBMITTED = a form filled but never committed. Off by default: it is not
     work to chase and it drowns the inbox. On when the host needs it — a parent
     can pay mid-form and never finish, and then the row has to be findable so
     they can be told to resume where they left off. */
  const [showUnsubmitted, setShowUnsubmitted] = React.useState(false);
  /* CLOSED = a registration the host withdrew. Off by default for the same
     reason: closing one already takes it off the roster, out of invoicing and
     out of sibling pricing, so a family who pulled out should not still be in
     the inbox adding to "12 shown". On when they need to find one and reopen
     it. */
  const [showClosed, setShowClosed] = React.useState(false);
  /* PAST PROGRAMS = registrations for a program the host has archived. Off by
     default, because that is the entire point of archiving one: a form filled
     in for a holiday club that already ran is history, not work, and leaving it
     here is how a host ends up scrolling past September to find October. On
     when they want the history back — looking up what a family did last term,
     or exporting the whole year. */
  const [showArchivedPrograms, setShowArchivedPrograms] = React.useState(false);

  const query = useQuery({
    queryKey: [
      "admin",
      "enrolments",
      status,
      showUnsubmitted,
      showClosed,
      showArchivedPrograms,
    ],
    queryFn: () =>
      api.listEnrolments({
        ...(status === "all" ? {} : { payment_status: status }),
        ...(showUnsubmitted ? { include_unsubmitted: true } : {}),
        ...(showClosed ? { include_closed: true } : {}),
        ...(showArchivedPrograms ? { include_archived_programs: true } : {}),
      }),
    staleTime: 30 * 1000,
    retry: 1,
  });

  // Programs resolve each enrolment's program_id to a readable name (and feed
  // the program filter). Includes hidden/past programs so old rows still label.
  const programsQuery = useQuery({
    queryKey: ["admin", "programs"],
    queryFn: () => api.adminListPrograms(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const programs = React.useMemo(
    () =>
      [...(programsQuery.data ?? [])].sort(
        (a, b) => a.sort_order - b.sort_order || a.id - b.id,
      ),
    [programsQuery.data],
  );
  const programById = React.useMemo(() => {
    const m = new Map<string, Program>();
    for (const p of programs) m.set(String(p.id), p);
    return m;
  }, [programs]);
  const programName = React.useCallback(
    (id: string | null | undefined): string => {
      if (id === null || id === undefined || id === "") return "";
      return programById.get(id)?.title ?? `Program #${id}`;
    },
    [programById],
  );

  // Form definitions drive the correction screen's answer list, so a question
  // the family skipped can still be filled in. Every form is fetched once and
  // narrowed to the one the enrolment's program uses.
  const formsQuery = useQuery({
    queryKey: ["admin", "forms"],
    queryFn: () => api.adminListForms(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const correctingFields = React.useMemo(() => {
    if (!correcting) return [];
    const formKey = correcting.program_id
      ? (programById.get(correcting.program_id)?.form_key ?? "enrolment")
      : "enrolment";
    return (formsQuery.data ?? []).filter((f) => f.form_key === formKey);
  }, [correcting, formsQuery.data, programById]);

  /* Whether this site can offer the move tool at all. It needs a /move/confirm
     route for the parent's link to land on, which today only apps/site has —
     see the flag's note in ../config.tsx for what htbh still needs. */
  const { canMoveRegistrations } = useEnrolmentUiConfig();

  /* Moves awaiting a parent's click. A registration with one out is in an
     in-between state the host has to be able to SEE — otherwise they re-ask,
     or wonder why the address on the row has not changed yet. */
  const openMovesQuery = useQuery({
    queryKey: ["admin", "enrolment-moves", "open"],
    queryFn: () => api.adminListEnrolmentMoves({ open: true }),
    staleTime: 30 * 1000,
    retry: 1,
    enabled: canMoveRegistrations,
  });
  const pendingMoveByEnrolment = React.useMemo(() => {
    const m = new Map<number, EnrolmentMove>();
    for (const move of openMovesQuery.data ?? []) m.set(move.enrolment_id, move);
    return m;
  }, [openMovesQuery.data]);

  const rows = React.useMemo(() => {
    let all = query.data ?? [];
    if (programFilter !== "all") {
      all = all.filter((e) => (e.program_id ?? "") === programFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      all = all.filter((e) =>
        [
          e.parent_name,
          e.parent_email,
          /* As stored, as displayed, and as bare digits — "0412 345" and
             "0412345678" must both find the same family. */
          e.parent_phone ?? "",
          formatPhone(e.parent_phone),
          String(e.parent_phone ?? "").replace(/\D/g, ""),
          e.child_name,
          e.year_level,
          e.payment_ref,
          e.program_id ?? "",
          programName(e.program_id),
        ]
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    /* Sorting comes last, on a COPY: the query's array is cached by react-query
       and shared with anything else reading it. The server's "newest first" is
       left exactly as it arrived rather than re-sorted by date here, so the
       default order can never disagree with the one the export used. */
    if (sort === "newest") return all;
    const sorted = [...all];
    if (sort === "parent_last") sorted.sort((a, b) => compareByLastName(a.parent_name, b.parent_name));
    else if (sort === "child_last") sorted.sort((a, b) => compareByLastName(a.child_name, b.child_name));
    else sorted.sort((a, b) => compareByFirstName(a.child_name, b.child_name));
    return sorted;
  }, [query.data, search, programFilter, programName, sort]);

  function exportCsv() {
    /* One column per question, instead of every answer squashed into a single
       "extra" cell of JSON. A host exporting this is nearly always about to
       hand ONE part of it to someone — dietary needs to catering, photo
       permissions to whoever runs social — and that is a column operation, not
       a JSON-parsing exercise.

       The columns come from @alight/contracts because the live spreadsheet
       feed (the "Link to a spreadsheet" button) builds the SAME sheet in the
       Worker. Two copies of this would drift the first time a host added a
       question, and the download and the linked sheet would disagree about the
       same registrations. See enrolmentAnswerColumns for the ordering. */
    const { columns, rows: cells } = enrolmentCsvTable(rows, formsQuery.data ?? [], programs);
    downloadCsv("enrolments", columns, cells);
  }

  const statusOptions: StatusFilter[] = ["all", ...PaymentStatus.options];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          Enrol a walk-up or a returning family yourself — they land on the roster straight away.
        </p>
        <div className="flex items-center gap-2">
          {/* Sits beside Export CSV's job, not inside the Toolbar: this is the
              answer to "I keep exporting the same file", so it has to be
              visible BEFORE the host reaches for the export button again. */}
          <Button type="button" variant="outline" size="sm" onClick={() => setLinkOpen(true)}>
            <TableProperties className="h-4 w-4" />
            Link to a spreadsheet
          </Button>
          <Button type="button" size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Add enrolment
          </Button>
        </div>
      </div>

      <ManualEnrolmentDialog open={addOpen} onOpenChange={setAddOpen} />
      <SheetLinkDialog api={api} open={linkOpen} onOpenChange={setLinkOpen} programs={programs} />

      <Toolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, email, child, ref…"
        onExport={exportCsv}
        exportDisabled={rows.length === 0}
        count={rows.length}
        filter={
          <>
            <Select value={programFilter} onValueChange={setProgramFilter}>
              <SelectTrigger className="w-56" aria-label="Filter by program">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All programs</SelectItem>
                {programs.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as StatusFilter)}
            >
              <SelectTrigger className="w-48" aria-label="Filter by payment status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "all" ? "All statuses" : statusLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Sorts the rows the EXPORT uses too, so the downloaded file and
                the screen are in the same order — a host who sorted by surname
                to read the list almost always wants the file that way as well. */}
            <Select value={sort} onValueChange={(v) => setSort(v as NameSort)}>
              <SelectTrigger className="w-52" aria-label="Sort registrations">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(NAME_SORT_LABELS) as NameSort[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {NAME_SORT_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* The "show me the hidden ones" switches sit together and wrap as
                a group, so none of them is squeezed onto two lines beside
                the dropdowns. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-primary"
                  checked={showUnsubmitted}
                  onChange={(e) => setShowUnsubmitted(e.target.checked)}
                />
                Also show unsubmitted
              </label>
              <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-primary"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                />
                Also show closed
              </label>
              <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-primary"
                  checked={showArchivedPrograms}
                  onChange={(e) => setShowArchivedPrograms(e.target.checked)}
                />
                Also show past programs
              </label>
            </div>
          </>
        }
      />

      {showUnsubmitted && (
        <p className="rounded-card border border-border bg-paper px-3 py-2 text-sm text-muted">
          <strong className="font-semibold text-ink">Unsubmitted</strong> rows are forms someone
          started but never sent — usually abandoned, occasionally a family who paid partway
          through. Their answers are still saved: ask them to open the registration page again
          and choose <strong className="font-semibold text-ink">Resume</strong>, and they will
          pick up where they left off.
        </p>
      )}

      {showClosed && (
        <p className="rounded-card border border-border bg-paper px-3 py-2 text-sm text-muted">
          <strong className="font-semibold text-ink">Closed</strong> rows are registrations you
          withdrew — a family who pulled out, or a row that was never really a registration. They
          are kept for the record but count towards nothing: not the rolls, not invoicing, not
          sibling pricing, and not the spreadsheet link. Use{" "}
          <strong className="font-semibold text-ink">Reopen</strong> on a row to put it back on
          the active list.
        </p>
      )}

      {showArchivedPrograms && (
        <p className="rounded-card border border-border bg-paper px-3 py-2 text-sm text-muted">
          <strong className="font-semibold text-ink">Past programs</strong> are the ones you have
          archived — last term’s class, a holiday club that has already run. Their registrations
          are normally hidden here, so this list stays the things that still need doing. Nothing
          is deleted: tick this box whenever you want the history back, for looking up what a
          family did last term or exporting the whole year.
        </p>
      )}

      <QueryStates
        isLoading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && rows.length === 0}
        /* An empty inbox needs to say WHY it is empty. A host whose only
           program has been archived would otherwise see nothing at all here
           and reasonably conclude the registrations were lost — the banner
           above only appears once the box is already ticked, which is too
           late to be the explanation. */
        emptyLabel={
          showArchivedPrograms
            ? "No enrolments match the current filter."
            : "No enrolments match the current filter. Registrations for past programs are hidden — tick “Also show past programs” to include them."
        }
      />

      {!query.isLoading && rows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Child</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="sticky right-0 z-10 bg-card text-right">
                    Details
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDay(e.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-ink">{e.parent_name}</div>
                      <div className="text-xs text-muted">{e.parent_email}</div>
                      {pendingMoveByEnrolment.has(e.id) ? (
                        <div className="mt-1">
                          <MovePendingBadge move={pendingMoveByEnrolment.get(e.id)!} />
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-ink">{e.child_name}</span>
                        {/* A leader registered THEMSELVES. Saying so here is
                            what stops an adult volunteer reading as a student
                            whose year level someone forgot to fill in. */}
                        {e.registrant_kind === "leader" && (
                          <Badge variant="accent">Leader</Badge>
                        )}
                        {/* CAME IN AFTER THE CLOSING DATE, on one of the host's
                            late-registration links. The server stamps this and
                            a browser cannot fake it. It used to be recorded and
                            shown nowhere, which is exactly how two late
                            registrations turned into an afternoon of "how did
                            they get through?" — the mark only helps if it is on
                            the row the host is already looking at. */}
                        {isLateRego(e) && (
                          <Badge
                            variant="warning"
                            title="Registered after the closing date, on a late registration link"
                          >
                            Late rego
                          </Badge>
                        )}
                      </div>
                      <div
                        className="max-w-[14rem] truncate text-xs text-muted"
                        title={e.program_id ? programName(e.program_id) : undefined}
                      >
                        {e.year_level ? `${e.year_level} · ` : ""}
                        {e.program_id ? programName(e.program_id) : "program not recorded"}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-ink">
                      {e.payment_ref}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink">
                      ${(e.amount_cents / 100).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {/* An unsubmitted row is not a registration, whatever
                          its payment status says — label it as what it is, so
                          nobody chases a family who never finished. */}
                      {e.status === "draft" ? (
                        <Badge variant="muted" title="Form started but never submitted">
                          Unsubmitted
                        </Badge>
                      ) : e.status === "withdrawn" ? (
                        /* Closed rows used to show their payment badge, so a
                           withdrawn registration read as "Unpaid" and got
                           chased. What matters about it is that it is closed. */
                        <Badge variant="muted" title="Closed — no longer counted">
                          Closed
                        </Badge>
                      ) : (
                        <Badge variant={statusVariant(e.payment_status)}>
                          {statusLabel(e.payment_status)}
                        </Badge>
                      )}
                    </TableCell>
                    {/* EVERY ACTION SAYS WHAT IT IS, IN WORDS — but in a menu,
                        not as five buttons wrapping onto three lines, which made
                        one child as tall as a paragraph. The menu keeps the word
                        AND the note naming the consequence (who gets emailed,
                        what cannot be undone), and gives the row back its
                        single line. See components/RowActions. */}
                    <TableCell className="sticky right-0 z-10 bg-card text-right">
                      <RowActions label={`Actions for ${e.child_name}`}>
                        <RowAction
                          icon={Eye}
                          label="View"
                          description="See everything this family submitted, and the record's edit history."
                          onSelect={() => setViewing(e)}
                        />
                        <RowAction
                          icon={Pencil}
                          label="Correct"
                          description="Fix a typo in what this family submitted — name, year level or answers. Nobody is emailed."
                          onSelect={() => setCorrecting(e)}
                        />
                        {/* Separate from "correct", because it is a different
                            act: correcting rewrites what this family typed,
                            moving hands the child to another family — and only
                            that family can agree to it. Hidden on a site with no
                            /move/confirm page: it would send a parent a link to
                            a 404. */}
                        {canMoveRegistrations && (
                          <RowAction
                            icon={AtSign}
                            label="Move to another email"
                            description="Hand this registration to a different parent's email. Sends that parent an email asking them to confirm."
                            onSelect={() => setMoving(e)}
                          />
                        )}
                        {/* CLOSE is the answer for a real family, and DELETE
                            for a row that should never have existed. Without
                            this there was only the destructive one, and the
                            delete dialog told the host to "mark the
                            registration withdrawn instead" — advice no screen
                            could act on. Reversible, so a mis-click costs
                            nothing. */}
                        {/* UNSUBMITTED, BUT REAL.
                            A form filled in properly whose payment was never
                            finished sits as a draft — invisible to the family,
                            outside the rolls, and swept away the moment a
                            sibling pays. When the host can see the family
                            meant it, this is how they say so: the registration
                            becomes ordinary and unpaid, chaseable like any
                            other, with nothing about the money touched. */}
                        {e.status === "draft" && (
                          <RowAction
                            icon={CheckCircle2}
                            label="Mark as submitted"
                            description="Treat this as a real registration that simply hasn't been paid yet. It joins the rolls and the unpaid list. Nobody is emailed."
                            onSelect={() => setClosing(e)}
                          />
                        )}
                        {e.status === "withdrawn" ? (
                          <RowAction
                            icon={RotateCcw}
                            label="Reopen"
                            description="Put this registration back on the active list. Nobody is emailed."
                            onSelect={() => setClosing(e)}
                          />
                        ) : (
                          <RowAction
                            icon={Ban}
                            label="Close"
                            description="A family who pulled out, or a form never finished. It stays in the records and stops counting toward rolls, numbers and sibling pricing. Nobody is emailed, and you can reopen it."
                            onSelect={() => setClosing(e)}
                          />
                        )}
                        {/* Removes the registration outright. Kept last and
                            marked destructive because it is the only one here
                            that destroys a record rather than editing it — it
                            exists so the host can clear the test rows they made
                            while learning the form. */}
                        <RowAction
                          icon={Trash2}
                          label="Delete"
                          description="Permanently erase this registration and its payment history. Cannot be undone — for test rows only."
                          destructive
                          onSelect={() => setDeleting(e)}
                        />
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <EnrolmentDetailsDialog
        key={viewing?.id ?? "none-view"}
        api={api}
        enrolment={viewing}
        programLabel={viewing?.program_id ? programName(viewing.program_id) : "Not recorded"}
        onClose={() => setViewing(null)}
      />

      <EditEnrolmentDialog
        key={correcting?.id ?? "none-correct"}
        api={api}
        enrolment={correcting}
        programLabel={
          correcting?.program_id ? programName(correcting.program_id) : "this program"
        }
        formFields={correctingFields}
        onClose={() => setCorrecting(null)}
        // Omitted where moving is unavailable, so the "already belongs to
        // another family" error does not offer a way out that does not exist.
        onMoveInstead={
          canMoveRegistrations
            ? (enrolment) => {
                setCorrecting(null);
                setMoving(enrolment);
              }
            : undefined
        }
      />

      {canMoveRegistrations && (
        <MoveRegistrationDialog
          key={moving?.id ?? "none-move"}
          api={api}
          enrolment={moving}
          onClose={() => setMoving(null)}
        />
      )}

      <CloseEnrolmentDialog
        key={closing?.id ?? "none-close"}
        api={api}
        enrolment={closing}
        programLabel={closing?.program_id ? programName(closing.program_id) : "this program"}
        onClose={() => setClosing(null)}
      />

      <DeleteEnrolmentDialog
        key={deleting?.id ?? "none-del"}
        api={api}
        enrolment={deleting}
        programLabel={deleting?.program_id ? programName(deleting.program_id) : "Not recorded"}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}

/** Close (withdraw) or reopen ONE registration.
 *
 *  The reversible counterpart to deleting. A closed registration keeps its row,
 *  its money and its history, and simply stops counting: rolls, headcounts and
 *  the family-discount ladder all exclude `withdrawn`, which is what makes this
 *  the right tool for a family who pulled out and for a form that was filled in
 *  and never sent. Deleting is for rows that should never have existed at all.
 *
 *  No typed confirmation, deliberately: nothing is destroyed and the same
 *  button puts it straight back, so the cost of a mis-click is one more click.
 *  Nobody is emailed — `PATCH /api/admin/enrolments/:id` never sends mail. */
function CloseEnrolmentDialog({
  api,
  enrolment,
  programLabel,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  enrolment: Enrolment | null;
  programLabel: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const reopening = enrolment?.status === "withdrawn";
  /* An unsubmitted form the host has decided is real. Same destination as a
     reopen ('new'), but a different act and a different explanation — nothing
     is being put BACK, it is arriving for the first time. */
  const promoting = enrolment?.status === "draft";

  const save = useMutation({
    /* Reopening returns the row to 'new' rather than to whatever it was before:
       nothing records the previous status, and 'new' is the state the host can
       read and act on. A confirmed registration reconfirmed is a smaller
       surprise than one that silently comes back marked paid-and-done. */
    mutationFn: () =>
      api.adminUpdateEnrolment(enrolment!.id, {
        status: reopening || promoting ? "new" : "withdrawn",
      }),
    onSuccess: () => {
      // Same three caches as delete: the roll and the students list both count
      // enrolments, and a closed one stops counting.
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      onClose();
    },
  });

  const hasMoney =
    enrolment !== null &&
    (enrolment.payment_status === "paid" || enrolment.payment_status === "partial");

  return (
    <Dialog open={enrolment !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {promoting
              ? "Mark this as submitted?"
              : reopening
                ? "Reopen this registration?"
                : "Close this registration?"}
          </DialogTitle>
          <DialogDescription>
            {promoting
              ? "This treats the form as a real registration that simply hasn't been paid yet. It appears in the family's own list, counts toward rolls, headcounts and sibling pricing, and shows up as unpaid so you can chase it. Nothing about the money changes and nobody is emailed."
              : reopening
                ? "This puts the registration back on the active list. It will count toward rolls, headcounts and sibling pricing again."
                : "This marks the registration closed. Nothing is deleted — it keeps its reference, its payment history and its answers — but it stops counting toward rolls, headcounts and sibling pricing. Nobody is emailed, and you can reopen it from the same button."}
          </DialogDescription>
        </DialogHeader>

        {enrolment ? (
          <div className="rounded-card border border-border bg-paper px-3 py-2">
            <DetailGrid
              fields={[
                ["Child", enrolment.child_name],
                ["Program", programLabel],
                ["Reference", enrolment.payment_ref],
                ["Amount", `$${(enrolment.amount_cents / 100).toFixed(2)}`],
                ["Payment", statusLabel(enrolment.payment_status)],
              ]}
            />
          </div>
        ) : null}

        {/* Closing does not refund. Said plainly, because "closed" reads like
            the money is settled and it is not — the host still has to send it
            back from the bank or Stripe themselves. */}
        {!reopening && !promoting && hasMoney ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-card border border-border bg-paper px-3 py-2 text-xs text-ink"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg" aria-hidden />
            <p>
              This registration has been paid. Closing it does not refund anything — the payment
              stays recorded against it, and any money owed back has to be returned from your bank
              or Stripe.
            </p>
          </div>
        ) : null}

        {save.error ? (
          <p className="text-sm text-accent-fg" role="alert">
            {save.error instanceof ApiError
              ? save.error.message
              : "Couldn’t save — please retry."}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending
              ? promoting
                ? "Marking…"
                : reopening
                  ? "Reopening…"
                  : "Closing…"
              : promoting
                ? "Mark as submitted"
                : reopening
                  ? "Reopen registration"
                  : "Close registration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Permanent-delete confirmation for ONE registration. Same typed-"DELETE"
 *  guard as the students page uses, for the same reason: the row next to it in
 *  the table belongs to a real family, and a mis-click here is not recoverable.
 *
 *  This is NOT a withdrawal — there is no record left saying the family pulled
 *  out, because the row is gone. It is for the test registrations a host makes
 *  while trying the form out. The dialog spells out exactly which record is
 *  going, and says so louder when money is attached to it. */
function DeleteEnrolmentDialog({
  api,
  enrolment,
  programLabel,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  enrolment: Enrolment | null;
  programLabel: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = React.useState("");

  const del = useMutation({
    mutationFn: () => api.adminDeleteEnrolment(enrolment!.id),
    onSuccess: () => {
      // The roll and the students list both count enrolments, so they go stale
      // the moment one disappears — refetch all three, not just this table.
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      onClose();
    },
  });

  /* "Money touched this row" is broader than payment_status === 'paid': a
     part-paid registration has receipts against it too, and erasing those is
     the thing the host can't undo from the bank's side. */
  const hasMoney =
    enrolment !== null &&
    (enrolment.payment_status === "paid" || enrolment.payment_status === "partial");
  const canDelete = confirm.trim().toUpperCase() === "DELETE";

  return (
    <Dialog open={enrolment !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this registration?</DialogTitle>
          <DialogDescription>
            This permanently removes the registration below, along with its attendance marks,
            payment records, invoice line, any pending email-change request and its correction
            history. It cannot be undone. {enrolment?.child_name ?? "The child"}’s student record
            stays — only this registration goes.
          </DialogDescription>
        </DialogHeader>

        {/* Named in full rather than "this registration": the host is deleting
            from a table of near-identical rows, and the ref is the only part
            that is unique. Same grid the View dialog uses, so it reads as the
            record they were just looking at. */}
        {enrolment ? (
          <div className="rounded-card border border-border bg-paper px-3 py-2">
            <DetailGrid
              fields={[
                ["Child", enrolment.child_name],
                ["Program", programLabel],
                ["Reference", enrolment.payment_ref],
                ["Amount", `$${(enrolment.amount_cents / 100).toFixed(2)}`],
                ["Payment", statusLabel(enrolment.payment_status)],
              ]}
            />
          </div>
        ) : null}

        {hasMoney ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-card border border-accent bg-accent/10 px-3 py-2 text-xs text-ink"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold">This registration has money against it.</p>
              <p>
                Deleting it erases the receipts as well, so the payment will no longer reconcile
                against anything — the bank or Stripe record will still exist with nothing here to
                match it. If this is a real family who pulled out, use{" "}
                <span className="font-semibold">Close</span> instead — it keeps the record and the
                receipts, and can be undone.
              </p>
            </div>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="delete-enrolment-confirm">
            Type <span className="font-mono font-semibold">DELETE</span> to confirm
          </Label>
          <Input
            id="delete-enrolment-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
          />
        </div>

        {del.error ? (
          <p className="text-sm text-accent-fg" role="alert">
            {del.error instanceof ApiError ? del.error.message : "Couldn’t delete — please retry."}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="accent"
            disabled={!canDelete || del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnrolmentDetailsDialog({
  api,
  enrolment,
  programLabel,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  /** Null when no row is being viewed — the dialog opens from the row menu. */
  enrolment: Enrolment | null;
  programLabel: string;
  onClose: () => void;
}) {
  if (enrolment === null) return null;

  const fields: Array<[string, string]> = [
    ["Reference", enrolment.payment_ref],
    ["Received", formatDate(enrolment.created_at)],
    ["Parent name", enrolment.parent_name],
    ["Parent email", enrolment.parent_email],
    // Grouped the same way the export writes it — see formatPhone.
    ["Parent phone", formatPhone(enrolment.parent_phone) || "—"],
    ["Child", enrolment.child_name],
    ["Child record", enrolment.child_id ? `#${enrolment.child_id}` : "—"],
    ["Year level", enrolment.year_level],
    ["Program", programLabel],
    ["Amount", `$${(enrolment.amount_cents / 100).toFixed(2)}`],
    [
      "Payment method",
      enrolment.payment_method
        ? METHOD_LABEL[enrolment.payment_method] ?? enrolment.payment_method
        : "—",
    ],
    ["Payment status", statusLabel(enrolment.payment_status)],
    ["Enrolment status", enrolment.status],
    /* Only when it IS one — an ordinary registration should not carry a row
       saying it arrived normally, or the exceptional case stops standing out. */
    ...(isLateRego(enrolment)
      ? ([
          [
            "How it came in",
            "Late registration link — after the closing date, on a link issued to this email",
          ],
        ] as Array<[string, string]>)
      : []),
    ["Paid at", enrolment.paid_at ? formatDate(enrolment.paid_at) : "—"],
    ["Paid by", enrolment.paid_by ?? "—"],
  ];

  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enrolment {enrolment.payment_ref}</DialogTitle>
          <DialogDescription>
            {enrolment.child_name} — {programLabel} — submitted by{" "}
            {enrolment.parent_name}
          </DialogDescription>
        </DialogHeader>
        <DetailGrid fields={fields} />
        <ExtraJson extra={enrolment.extra} />
        <EditHistory api={api} entity="enrolment" entityId={enrolment.id} />
      </DialogContent>
    </Dialog>
  );
}

/* ===========================================================================
 * Contact messages
 * ========================================================================= */

/** A message has one person on it, so its sort is the same idea with fewer
 *  choices than the enrolments table. */
type ContactSort = "newest" | "last" | "first";

const CONTACT_SORT_LABELS: Record<ContactSort, string> = {
  newest: "Newest first",
  last: "Surname A–Z",
  first: "First name A–Z",
};

function ContactPanel() {
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState<ContactSort>("newest");

  const query = useQuery<ContactMessageT[]>({
    queryKey: ["admin", "contact-messages"],
    queryFn: fetchContactMessages,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const rows = React.useMemo(() => {
    let all = query.data ?? [];
    const q = search.trim().toLowerCase();
    if (q) {
      all = all.filter((m) =>
        [m.name, m.email, m.subject, m.message, m.status]
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    if (sort === "newest") return all;
    const sorted = [...all];
    sorted.sort((a, b) =>
      sort === "last" ? compareByLastName(a.name, b.name) : compareByFirstName(a.name, b.name),
    );
    return sorted;
  }, [query.data, search, sort]);

  function exportCsv() {
    downloadCsv(
      "contact-messages",
      [
        "id",
        "created_at",
        "name",
        // Derived beside the full name, exactly as the registrations sheet does
        // it — the same file opens in the same tool and gets sorted the same way.
        "first_name",
        "last_name",
        "email",
        "subject",
        "message",
        "status",
        "extra",
      ],
      rows.map((m) => {
        const { first, last } = splitPersonName(m.name);
        return {
          id: m.id,
          created_at: m.created_at,
          name: m.name,
          first_name: first,
          last_name: last,
          email: m.email,
          subject: m.subject,
          message: m.message,
          status: m.status,
          extra: JSON.stringify(m.extra ?? {}),
        };
      }),
    );
  }

  return (
    <div className="space-y-4">
      <Toolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, email, subject…"
        onExport={exportCsv}
        exportDisabled={rows.length === 0}
        count={rows.length}
        filter={
          <Select value={sort} onValueChange={(v) => setSort(v as ContactSort)}>
            <SelectTrigger className="w-44" aria-label="Sort messages">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(CONTACT_SORT_LABELS) as ContactSort[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {CONTACT_SORT_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <QueryStates
        isLoading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && rows.length === 0}
        emptyLabel="No contact messages found."
      />

      {!query.isLoading && rows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="sticky right-0 z-10 bg-card text-right">
                    Details
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDay(m.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-ink">{m.name}</div>
                      <div className="text-xs text-muted">{m.email}</div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-ink" title={m.subject}>
                      {m.subject}
                    </TableCell>
                    <TableCell>
                      <Badge variant="muted">{m.status}</Badge>
                    </TableCell>
                    <TableCell className="sticky right-0 z-10 bg-card text-right">
                      <ContactDetailsDialog message={m} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ContactDetailsDialog({ message }: { message: ContactMessageT }) {
  const fields: Array<[string, string]> = [
    ["Received", formatDate(message.created_at)],
    ["Name", message.name],
    ["Email", message.email],
    ["Subject", message.subject],
    ["Status", message.status],
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          View
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{message.subject}</DialogTitle>
          <DialogDescription>
            From {message.name} ({message.email})
          </DialogDescription>
        </DialogHeader>
        <DetailGrid fields={fields} />
        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">
            Message
          </div>
          <p className="whitespace-pre-wrap rounded-card border border-border bg-paper p-3 text-sm text-ink">
            {message.message}
          </p>
        </div>
        <ExtraJson extra={message.extra} />
      </DialogContent>
    </Dialog>
  );
}

async function fetchContactMessages(): Promise<ContactMessageT[]> {
  const res = await fetch(`${API_BASE}${CONTACT_MESSAGES_PATH}`, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  const text = await res.text();
  if (!res.ok) {
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : undefined;
    } catch {
      body = text;
    }
    throw new ApiError(res.status, `GET ${CONTACT_MESSAGES_PATH} failed`, body);
  }
  const json: unknown = text ? JSON.parse(text) : [];
  return z.array(ContactMessage).parse(json);
}

/* ===========================================================================
 * Shared presentational helpers
 * ========================================================================= */

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        "-mb-px border-b-2 px-3 pb-2 text-sm font-semibold transition-colors " +
        (active
          ? "border-primary text-primary"
          : "border-transparent text-muted hover:text-ink")
      }
    >
      {children}
    </button>
  );
}

function Toolbar({
  search,
  onSearch,
  searchPlaceholder,
  onExport,
  exportDisabled,
  count,
  filter,
}: {
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder: string;
  onExport: () => void;
  exportDisabled: boolean;
  count: number;
  filter?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="sm:max-w-xs"
          aria-label="Search submissions"
        />
        {filter}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted">{count} shown</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExport}
          disabled={exportDisabled}
        >
          Export CSV
        </Button>
      </div>
    </div>
  );
}

function QueryStates({
  isLoading,
  error,
  empty,
  emptyLabel,
}: {
  isLoading: boolean;
  error: unknown;
  empty: boolean;
  emptyLabel: string;
}) {
  if (isLoading) {
    return (
      <div className="rounded-card border border-border bg-paper px-4 py-8 text-center text-sm text-muted">
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div
        role="alert"
        className="rounded-card border border-error bg-error/10 px-4 py-3 text-sm text-ink"
      >
        Couldn't load submissions{error instanceof ApiError ? ` (${error.status})` : ""}.
        Please retry.
      </div>
    );
  }
  if (empty) {
    return (
      <div className="rounded-card border border-border bg-paper px-4 py-8 text-center text-sm text-muted">
        {emptyLabel}
      </div>
    );
  }
  return null;
}

function DetailGrid({ fields }: { fields: Array<[string, string]> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      {fields.map(([label, value]) => (
        <div key={label} className="flex flex-col">
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
            {label}
          </dt>
          <dd className="break-words text-sm text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Journey/system keys that get friendly labels; everything else is a
 *  host-added form field and is title-cased from its snake_case name. */
const EXTRA_LABELS: Record<string, string> = {
  cash_when: "Cash handover",
  already_note: "How they paid",
  /* "No Shirt: Yes" is a double negative to read at a glance, and this row is
     what the shirt order is counted off. */
  [NO_SHIRT_ANSWER]: "Shirt not needed",
};
/** Internal keys never shown to the host (they aren't submission content).
 *  `late_registration` is not hidden because it doesn't matter — it is hidden
 *  because it now has a "Late rego" badge on the row and a line of its own in
 *  the details, and a third copy buried among the form answers is noise. */
const EXTRA_HIDDEN = new Set(["turnstileToken", "late_registration"]);

/**
 * Did this registration come in after the closing date, on one of the host's
 * late-registration links? Stamped by the Worker on submit (handlers/enrol.ts)
 * and deleted from anything the browser sends, so it cannot be self-awarded.
 */
function isLateRego(enrolment: { extra?: Record<string, unknown> | null }): boolean {
  return enrolment.extra?.late_registration === true;
}

function humanizeKey(key: string): string {
  if (EXTRA_LABELS[key]) return EXTRA_LABELS[key];
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\w/g, (c) => c.toUpperCase())
    .trim();
}

function renderExtraValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Additional submission fields — the journey's cash_when/already_note plus any
 * host-added form fields — rendered as a CLEAN labelled list (not raw JSON).
 * Internal system keys (e.g. the Turnstile token) are hidden.
 */
function ExtraJson({ extra }: { extra: Record<string, unknown> | undefined }) {
  const entries = Object.entries(extra ?? {}).filter(([k]) => !EXTRA_HIDDEN.has(k));
  if (entries.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">
        Additional details
      </div>
      <dl className="divide-y divide-border rounded-card border border-border bg-paper">
        {entries.map(([key, value]) => (
          <div key={key} className="grid grid-cols-[10rem_1fr] gap-3 px-3 py-2 text-sm">
            <dt className="text-muted">{humanizeKey(key)}</dt>
            <dd className="text-ink">{renderExtraValue(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/* ===========================================================================
 * Pure helpers
 * ========================================================================= */

function statusLabel(s: PaymentStatusT): string {
  switch (s) {
    case "pending_review":
      return "Pending review";
    case "unpaid":
      return "Unpaid";
    case "paid":
      return "Paid";
    case "refunded":
      return "Refunded";
    case "cancelled":
      return "Cancelled";
    default:
      return s;
  }
}

function statusVariant(s: PaymentStatusT): BadgeVariant {
  switch (s) {
    case "paid":
      return "success";
    case "pending_review":
      return "accent";
    case "unpaid":
      return "muted";
    default:
      return "outline";
  }
}

type BadgeVariant = "default" | "accent" | "success" | "muted" | "outline";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Date only — keeps grid rows narrow; the exact time shows in the details dialog. */
function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Build a CSV string and trigger a client-side download. */
function downloadCsv(
  name: string,
  columns: string[],
  rows: Array<Record<string, string | number>>,
): void {
  // `bom` prefixes U+FEFF so desktop Excel reads the DOWNLOADED file as UTF-8.
  // (The live feed deliberately omits it — see toCsv.)
  const csv = toCsv(columns, rows, { bom: true });

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `alight-${name}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
